import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  AuthModule,
  getB2CMsalGuardConfig,
  getB2CMsalInstance,
  getB2CMsalInterceptorConfig,
  GetMsalInterceptorConfiguration,
  LoginCallbackComponent,
} from '@vinnova/auth';
import {
  chatbotApiUrl as CHATBOT_API_URL,
  ChatbotWidgetComponent,
} from '@vinnova/chatbot';
import { ApimInterceptor, ETjansterModule } from '@vinnova/e-tjanster';
import {
  FeatureService,
  FeatureTogglingModule,
  initializeFeatures,
} from '@vinnova/feature-toggling';
import {
  projektApiUrl as PROJEKT_API_URL,
  projektEnvProduction as PROJEKT_ENV_PRODUCTION,
} from '@vinnova/formrenderer';
import { LoggingModule } from '@vinnova/logging';
import { BankAccountClient, OregLibModule } from '@vinnova/oreg-lib';
import { TranslationModule } from '@vinnova/translation';
import { ToastModule } from '@vinnova/ui';
import * as i18nIsoCountries from 'i18n-iso-countries';
import * as en from 'i18n-iso-countries/langs/en.json';
import * as sv from 'i18n-iso-countries/langs/sv.json';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutComponent } from './layout/layout.component';
import { PdfLoadingComponent } from './pdf-loading/pdf-loading.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
i18nIsoCountries.registerLocale((sv as any).default);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
i18nIsoCountries.registerLocale(en as any);

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const interceptorConfig = getB2CMsalInterceptorConfig(
    environment.b2cMsalConfig,
    undefined,
    [
      {
        url: `${environment.etjansterCommonApiUrl}/account/loginb2c`,
        clientId: environment.b2cMsalConfig.clientId.etjansterCommonApiClientId,
      },
      {
        url: `${environment.oregApiUrl}/*`,
        clientId: environment.b2cMsalConfig.clientId.oregApiClientId,
      },
    ],
    [
      environment.resourceStringUrl,
      environment.featureUrl,
      `${environment.projektApiUrl}/ansokningsomgang/*/publication`,
    ],
  );
  return GetMsalInterceptorConfiguration(
    interceptorConfig,
    environment.b2cMsalConfig,
  );
}

@NgModule({
  declarations: [AppComponent, LayoutComponent, PdfLoadingComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ETjansterModule.forRoot(environment),
    FeatureTogglingModule.forRoot(environment),
    AuthModule.forRoot(environment, 'projekt'),
    TranslationModule.forRoot(environment),
    LoggingModule.forRoot(environment),
    ToastModule,
    MsalModule.forRoot(
      getB2CMsalInstance(environment.b2cMsalConfig),
      getB2CMsalGuardConfig(),
      undefined,
    ),
    OregLibModule.forRoot(environment, {
      provide: BankAccountClient,
      useClass: BankAccountClient,
    }),
    LoginCallbackComponent,
    ChatbotWidgetComponent,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApimInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    provideAppInitializer(() => {
      const initializerFn = initializeFeatures(inject(FeatureService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: PROJEKT_API_URL,
      useValue: environment.projektApiUrl,
    },
    {
      provide: PROJEKT_ENV_PRODUCTION,
      useValue: environment.production,
    },
    {
      provide: CHATBOT_API_URL,
      useValue: environment.chatbotApiUrl,
    },
  ],
})
export class AppModule {}
